import { useCallback, useEffect, useMemo, useState } from "react";
import { MapPin, Mail, RefreshCw } from "lucide-react";
import PageHeader, { ghostBtn, card } from "@/app/PageHeader";
import { useSession } from "@/app/session";

// Messages left through the QR feedback page, newest first.
const FeedbackInbox = () => {
  const { api, onAuthError } = useSession();
  const [items, setItems] = useState(null);
  const [location, setLocation] = useState("");

  const load = useCallback(() => {
    api.get("/feedback").then((r) => setItems(r.data)).catch((e) => onAuthError(e, "Could not load feedback."));
  }, [api, onAuthError]);
  useEffect(() => { load(); }, [load]);

  const locations = useMemo(() => [...new Set((items || []).map((f) => f.location_name).filter(Boolean))].sort(), [items]);
  const shown = (items || []).filter((f) => !location || f.location_name === location);

  return (
    <div data-testid="feedback-inbox">
      <PageHeader title="Feedback"
        intro="What people tell you by scanning the QR card on a machine. Reply by email when they leave an address."
        actions={<>
          {locations.length > 1 && (
            <select value={location} onChange={(e) => setLocation(e.target.value)} aria-label="Filter by location"
              className="h-9 rounded-full border border-white/15 bg-vortex-ink px-4 text-xs text-slate-300 focus:outline-none focus:ring-2 focus:ring-vortex-cyan">
              <option value="">All locations</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          <button onClick={load} className={ghostBtn}><RefreshCw size={14} /> Refresh</button>
        </>} />

      <div className={card}>
        {items === null ? (
          <p className="px-6 py-10 text-sm text-slate-500">Loading feedback...</p>
        ) : shown.length === 0 ? (
          <p className="px-6 py-10 text-sm text-slate-500" data-testid="feedback-inbox-empty">
            No feedback yet. Print a QR card from Locations &amp; QR and messages will land here.
          </p>
        ) : shown.map((fb) => (
          <div key={fb.id} className="border-b border-white/5 px-6 py-5 last:border-b-0" data-testid={`feedback-row-${fb.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-vortex-cyan/40 px-3 py-1 font-mono text-[10px] tracking-[0.15em] uppercase text-vortex-cyan">
                {fb.category}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-slate-200">
                <MapPin size={13} className="text-vortex-cyan" /> {fb.location_name}
              </span>
              <span className="ml-auto font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">
                {new Date(fb.created_at).toLocaleString()}
              </span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">{fb.message}</p>
            <p className="mt-2 flex items-center gap-2 text-xs text-slate-500">
              {fb.name || "Anonymous"}
              {fb.email && (
                <a href={`mailto:${fb.email}?subject=${encodeURIComponent("Your Vortex Vending feedback")}`}
                  className="flex items-center gap-1 text-vortex-cyan hover:underline">
                  <Mail size={12} /> {fb.email}
                </a>
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeedbackInbox;
